import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Lock, Layers } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import LeadDialog from "@/components/LeadDialog";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="reports-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-20 right-10 h-80 w-80 rounded-full bg-electric/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <span className="overline-label text-electric">Research & Reports</span>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold max-w-3xl leading-tight">
            Gated research that powers smarter decisions
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg">
            Benchmarks, surveys and white papers from the Lead ITES research desk. Unlock any report
            by sharing a few details — delivered straight to your inbox.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reports.map((rep, i) => (
              <motion.div key={rep.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                className="card-lift bg-white border border-border rounded-xl overflow-hidden grid grid-cols-1 sm:grid-cols-5"
                data-testid={`report-card-${rep.slug}`}>
                <div className="sm:col-span-2 aspect-video sm:aspect-auto bg-secondary overflow-hidden">
                  {rep.cover_image && <img src={rep.cover_image} alt={rep.title} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="sm:col-span-3 p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-navy text-white hover:bg-navy rounded-full text-[11px]">{rep.report_type}</Badge>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Layers className="h-3 w-3" /> {rep.pages} pages</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-navy leading-snug">
                    <Link to={`/reports/${rep.slug}`} className="hover:text-electric transition-colors">{rep.title}</Link>
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2 flex-1">{rep.summary}</p>
                  <div className="mt-5 flex items-center gap-3">
                    <LeadDialog
                      source="report"
                      reportSlug={rep.slug}
                      title={`Download: ${rep.title}`}
                      description="Fill in your details to receive this report. Our team may follow up with related research."
                      onGranted={(data) => { if (data?.download_url) window.open(fileUrl(data.download_url), "_blank"); }}
                      trigger={
                        <Button className="btn-electric rounded-full" data-testid={`download-report-${rep.slug}`}>
                          <Lock className="h-3.5 w-3.5" /> Unlock Report
                        </Button>
                      }
                    />
                    <Link to={`/reports/${rep.slug}`} className="text-sm font-semibold text-navy/70 hover:text-electric" data-testid={`view-report-${rep.slug}`}>
                      Details
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
